import InviteMembersModal from "@/src/components/action-modals/add-members/add-members-modal";
import ScButton from "@/src/components/ui-kit/button";
import ScSearchInput from "@/src/components/ui-kit/search-bar";
import ScTabs from "@/src/components/ui-kit/tabs";
import {
  EventItem,
  eventRegistrationPolicy,
  RSVPStatus,
} from "@/src/models/event.model";
import { useGetRSVPsQuery } from "@/src/services/api/events/events.hooks";
import { useTheme } from "@/src/styles/theme-provider";
import { exportParticipantsToExcel } from "@/src/utils/export-excel.utils";
import { ExportSquare, UserAdd } from "iconsax-react-nativejs";
import BackNavigationButton from "@/src/components/ui-kit/back-navigation";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FlatList, Text, View } from "react-native";
import { SectionCard } from "./event-detail-section-card";
import { ParticipantRow, ParticipantTableHeader } from "./participant-table";

type ParticipantsTab = "LABEL_TAB_PARTICIPANTS" | "LABEL_TAB_WAITLIST";

const PARTICIPANTS_TABS: readonly ParticipantsTab[] = [
  "LABEL_TAB_PARTICIPANTS",
  "LABEL_TAB_WAITLIST",
];

type EventParticipantsProps = Readonly<{
  event: EventItem;
  isAuthor: boolean;
  onClose: () => void;
}>;

const EventParticipants = ({
  event,
  isAuthor,
  onClose,
}: EventParticipantsProps) => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();

  const [showInvite, setShowInvite] = useState(false);
  const [activeTab, setActiveTab] = useState<ParticipantsTab>(
    "LABEL_TAB_PARTICIPANTS",
  );
  const [participantSearch, setParticipantSearch] = useState("");
  const [selectedParticipantIds, setSelectedParticipantIds] = useState<
    readonly string[]
  >([]);
  const [exporting, setExporting] = useState(false);

  const isInviteOnly =
    event.registrationPolicy === eventRegistrationPolicy.INVITE_ONLY;

  const { data: goingRSVPs } = useGetRSVPsQuery(event.ItemId, RSVPStatus.GOING);
  const { data: waitingRSVPs } = useGetRSVPsQuery(
    event.ItemId,
    RSVPStatus.WAITING,
  );
  const attendingList = goingRSVPs?.items ?? [];
  const waitlistedList = waitingRSVPs?.items ?? [];

  const currentTabList =
    activeTab === "LABEL_TAB_PARTICIPANTS" ? attendingList : waitlistedList;

  const searchedParticipants = currentTabList.filter((rsvp) => {
    const { firstName, lastName } = rsvp.eventParticipantSnapshot;
    const fullName = `${firstName} ${lastName}`.toLowerCase();
    return fullName.includes(participantSearch.toLowerCase());
  });
  const searchedParticipantIds = searchedParticipants.map(
    (rsvp) => rsvp.ItemId,
  );
  const isAllParticipantsSelected =
    searchedParticipantIds.length > 0 &&
    searchedParticipantIds.every((id) => selectedParticipantIds.includes(id));

  const toggleParticipant = (userId: string) => {
    setSelectedParticipantIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  const toggleAllParticipants = () => {
    setSelectedParticipantIds(
      isAllParticipantsSelected ? [] : searchedParticipantIds,
    );
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const tabStatus =
        activeTab === "LABEL_TAB_PARTICIPANTS" ? "attending" : "waitlisted";
      await exportParticipantsToExcel(event, tabStatus);
    } finally {
      setExporting(false);
    }
  };

  const handleClearSelection = () => setSelectedParticipantIds([]);
  const filteredCount = searchedParticipantIds.length;
  const selectedCount = selectedParticipantIds.length;

  return (
    <SectionCard>
      {/* Back */}
      <BackNavigationButton
        label={t("LABEL_BACK")}
        iconSize={16}
        iconClassName="text-ash-1"
        labelClassName="text-ash-1"
        onPress={onClose}
        containerStyle={{ marginBottom: 16, alignSelf: "flex-start" }}
      />

      {/* Tabs + Export */}
      <View className="flex-row items-center justify-between mb-4">
        <ScTabs
          tabs={PARTICIPANTS_TABS}
          active={activeTab}
          onChange={setActiveTab}
        />
        {isAuthor && (
          <ScButton
            text="BTN_EXPORT_LIST"
            mode="outlined"
            onPress={handleExport}
            loading={exporting}
            icon={<ExportSquare size={16} color={themeVars["--color-primary-default"] as string} />}
          />
        )}
      </View>

      {/* Search + Invite */}
      <View className="flex-row items-center gap-3 mb-3">
        <ScSearchInput
          value={participantSearch}
          placeholderKey="LABEL_SEARCH_PARTICIPANTS"
          onChangeText={setParticipantSearch}
          containerClassName="flex-1 rounded-xl"
        />
        {isAuthor && isInviteOnly && (
          <ScButton
            text="BTN_INVITE"
            mode="contained"
            onPress={() => setShowInvite(true)}
            icon={<UserAdd size={16} color={"#FFFFFF"} />}
          />
        )}
      </View>

      {/* Count */}
      <Text className="text-sm mb-2" style={{ color: themeVars["--color-ash-1"] as string }}>
        {t("LABEL_PARTICIPANT_COUNT", { count: filteredCount })}
      </Text>

      {/* Table header */}
      <ParticipantTableHeader
        isAuthor={isAuthor}
        isAllSelected={isAllParticipantsSelected}
        onToggleAll={toggleAllParticipants}
      />

      {/* List */}
      <FlatList
        data={searchedParticipants}
        keyExtractor={(rsvp) => rsvp.ItemId}
        scrollEnabled={false}
        ItemSeparatorComponent={() => (
          <View className="border-b border-ash-3" />
        )}
        renderItem={({ item: rsvp }) => (
          <ParticipantRow
            participant={rsvp.eventParticipantSnapshot}
            userId={rsvp.CreatedBy}
            selected={selectedParticipantIds.includes(rsvp.ItemId)}
            onToggle={() => toggleParticipant(rsvp.ItemId)}
            isAuthor={isAuthor}
          />
        )}
      />

      {/* Remove footer */}
      {isAuthor && selectedCount > 0 && (
        <View className="flex-row items-center justify-between pt-4 mt-2 border-t border-ash-3">
          <Text className="text-sm" style={{ color: themeVars["--color-ash-1"] as string }}>
            {t("LABEL_SELECTED_COUNT", { count: selectedCount })}
          </Text>
          <ScButton
            text="BTN_REMOVE"
            mode="outlined"
            onPress={handleClearSelection}
          />
        </View>
      )}

      {/* Invite modal */}
      <InviteMembersModal
        visible={showInvite}
        onClose={() => setShowInvite(false)}
        onInvite={(ids) => {
          // TODO: wire to API
        }}
      />
    </SectionCard>
  );
};

export default EventParticipants;
